import SectionHeader from '../components/ui/SectionHeader'

type DocSection = {
  id: string
  title: string
  subtitle?: string
  body: React.ReactNode
}

const curlStart = `curl -sS -X POST http://127.0.0.1:8081/api/v1/negotiation/start \\
  -H 'content-type: application/json' \\
  -d '{
    "source_asset": "SOL",
    "target_asset": "USDC_BASE",
    "amount": 1.0,
    "persona_digest": "demo",
    "x402_request": {
      "headers": {
        "www-authenticate": "X402 ..."
      },
      "body": {
        "paymentRequirement": {
          "recipient": "0xabc",
          "amountAtomic": "123",
          "decimals": 6,
          "expiresAt": "2030-01-01T00:00:00Z",
          "nonce": "example"
        }
      }
    }
  }'`

const curlCommit = `curl -sS -X POST http://127.0.0.1:8081/api/v1/negotiation/commit \\
  -H 'content-type: application/json' \\
  -d '{"session_id":"<session_id>"}'`

const curlAuthorize = `curl -sS -X POST http://127.0.0.1:8081/api/v1/orders/authorize \\
  -H 'content-type: application/json' \\
  -d '{
    "order_id": "<order_id>",
    "chain": "solana:mainnet",
    "message": "Authorize maxInput=<...> deadline=<...> orderId=<...>",
    "public_key": "<wallet_public_key>",
    "signature_base64": "<base64(signature_bytes)>"
  }'`

const exampleExecutionPlan = `{
  "order_id": "e2c3c2f2-....",
  "deadline_at": "2026-02-15T12:00:00Z",
  "max_input": "1030000000",
  "steps": [
    {
      "step_id": "step_1",
      "kind": "SOLANA_SWAP",
      "chain_caip2": "solana:mainnet",
      "provider": "jupiter",
      "tx_template": {
        "inputMint": "So11111111111111111111111111111111111111112",
        "outputMint": "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
        "amountAtomic": "1000000000",
        "slippageBps": 50,
        "taker": "<solana_pubkey>"
      }
    },
    {
      "step_id": "step_2",
      "kind": "CCTP_BURN_MINT",
      "chain_caip2": "solana:mainnet",
      "provider": "cctp",
      "tx_template": {
        "fromCaip2": "solana:mainnet",
        "toCaip2": "eip155:8453",
        "assetId": "USDC",
        "amountAtomic": "1000000",
        "recipient": "<evm_address>"
      }
    },
    {
      "step_id": "step_3",
      "kind": "EVM_PAY",
      "chain_caip2": "eip155:8453",
      "provider": "vault",
      "tx_template": {
        "assetId": "USDC",
        "amountAtomic": "1000000",
        "recipient": "<evm_address>"
      }
    }
  ]
}`

const exampleReceiptV2 = `{
  "orderId": "e2c3c2f2-....",
  "status": "SETTLED",
  "paymentTarget": {
    "caip2": "eip155:8453",
    "assetId": "USDC",
    "amountAtomic": "1000000",
    "recipient": "0xabc..."
  },
  "actualOutAtomic": "1000000",
  "transactions": [
    {
      "chainCaip2": "solana:mainnet",
      "txHash": "5F8v....",
      "kind": "SOLANA_SWAP",
      "provider": "jupiter",
      "confirmedAt": "2026-02-15T12:00:03Z"
    },
    {
      "chainCaip2": "solana:mainnet",
      "txHash": "server_....",
      "kind": "CCTP_BURN_MINT",
      "provider": "cctp",
      "confirmedAt": "2026-02-15T12:00:10Z"
    }
  ],
  "fees": {
    "platformFeeAtomic": "3000",
    "bridgeFeeAtomic": "0",
    "gasFeeAtomic": "0",
    "totalFeeAtomic": "3000"
  },
  "supplement": null,
  "x402Proof": {
    "provider": "third-party",
    "raw": { "paymentRequirement": { "...": "..." } }
  }
}`

const exampleA2AEnvelope = `{
  "messageId": "01J... (ulid)",
  "correlationId": "order:e2c3c2f2-....",
  "timestamp": "2026-02-15T12:00:00Z",
  "from": { "agent": "orchestrator", "service": "negotiation-server" },
  "to": { "agent": "quote-agent", "service": "quote-service" },
  "intent": {
    "name": "quote.route",
    "version": "v1",
    "idempotencyKey": "order:e2c3c2f2-....:quote:v1"
  },
  "constraints": {
    "maxTotalFeeBps": 150,
    "maxSlippageBps": 50,
    "maxDurationSeconds": 60,
    "deadlineAt": "2026-02-15T12:02:00Z"
  },
  "payload": {
    "source": { "caip2": "solana:mainnet", "assetId": "solana:mainnet/sol" },
    "target": { "caip2": "eip155:8453", "assetId": "eip155:8453/usdc", "amountAtomic": "1000000", "recipient": "0xabc..." }
  },
  "trace": { "spanId": "a1b2...", "parentSpanId": "p9q8..." }
}`

const exampleX402Nested = `{
  "paymentRequirement": {
    "caip2": "eip155:8453",
    "asset_id": "eip155:8453/usdc",
    "recipient": "0x0000000000000000000000000000000000000abc",
    "amountAtomic": "1000000",
    "decimals": 6,
    "expiresAt": "2030-01-01T00:00:00Z"
  }
}`

const exampleX402Array = `{
  "payment_requirements": [
    {
      "caip2": "eip155:8453",
      "asset_id": "eip155:8453/usdc",
      "recipient": "0x0000000000000000000000000000000000000001",
      "amountAtomic": "1000000",
      "decimals": 6
    },
    {
      "caip2": "eip155:137",
      "asset_id": "eip155:137/usdc",
      "recipient": "0x0000000000000000000000000000000000000002",
      "amountAtomic": "1000000",
      "decimals": 6
    }
  ]
}`

const exampleX402StrictErrors = `missing_recipient
invalid_recipient
invalid_amount_atomic
missing_target_chain
missing_target_asset
invalid_expires_at`

const exampleX401Challenge = `HTTP/1.1 401 Unauthorized
WWW-Authenticate: X401 realm="soulon-a2a", nonce="01J...", alg="ed25519", scope="quote.route"
Content-Type: application/json

{
  "error": "x401_required",
  "required": {
    "audience": "quote-service",
    "scope": "quote.route",
    "expiresInSeconds": 60
  }
}`

const exampleX401Authorization = `POST /a2a/invoke HTTP/1.1
Authorization: X401 keyId="agent:orchestrator#1", nonce="01J...", sig="base64(...)"
Content-Type: application/json

{ ...A2A envelope... }`

const exampleX401Canonical = `POST
/a2a/invoke
<nonce>
<sha256(body)_base64>
<keyId>`

const exampleX401KeysJson = `{
  "agent:orchestrator#1": {
    "publicKeyBase64": "<X509_public_key_base64>",
    "scopes": ["quote.route", "route.plan", "execute.step", "monitor.observe", "refund.issue"],
    "disabled": false
  }
}`

const exampleA2AStorageEnv = `X401_KEYS_JSON='...'
X401_NONCE_STORE_PATH='/tmp/x401_nonces.tsv'
A2A_IDEMPOTENCY_PATH='/tmp/a2a_idempotency.tsv'
AUDIT_LOG_PATH='/tmp/negotiation_audit.jsonl'`

const exampleNegotiationWebSocket = `wss://api.soulon.top/ws/negotiation/<sessionId>

{"round":1,"role":"buyer","content":"...","offer":null}
{"round":1,"role":"market_maker","content":"...","offer":{"fee_bps":120,"duration_seconds":60,"route_type":"CCTP_USDC"}}`

const curlX402Proof = `curl -sS http://127.0.0.1:8081/api/v1/x402/proof/<order_id> \\
  -H 'accept: application/json'`

const exampleReceiptFailed = `{
  "orderId": "e2c3c2f2-....",
  "status": "FAILED",
  "paymentTarget": { "caip2": "eip155:8453", "assetId": "USDC", "amountAtomic": "1000000", "recipient": "0xabc..." },
  "actualOutAtomic": "900000",
  "fees": { "platformFeeAtomic": "3000", "bridgeFeeAtomic": "0", "gasFeeAtomic": "0", "totalFeeAtomic": "3000" },
  "supplement": { "supplementAtomic": "100000", "supplementBps": 1000, "reasonCode": "SUPPLEMENT_LIMIT" },
  "transactions": []
}`

const exampleReceiptRefunded = `{
  "orderId": "e2c3c2f2-....",
  "status": "REFUNDED",
  "paymentTarget": { "caip2": "eip155:8453", "assetId": "USDC", "amountAtomic": "1000000", "recipient": "0xabc..." },
  "actualOutAtomic": "1000000",
  "fees": { "platformFeeAtomic": "0", "bridgeFeeAtomic": "0", "gasFeeAtomic": "0", "totalFeeAtomic": "0" },
  "supplement": { "supplementAtomic": "0", "supplementBps": 0, "reasonCode": "deadline_exceeded" },
  "transactions": []
}`

function CodeBlock(props: { title?: string; code: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20">
      {props.title ? <div className="border-b border-white/10 px-4 py-3 text-xs text-white/60">{props.title}</div> : null}
      <pre className="overflow-auto p-4 text-xs leading-relaxed text-white/75">
        <code>{props.code}</code>
      </pre>
    </div>
  )
}

function DocCard(props: { children: React.ReactNode }) {
  return <div className="glass2 p-7">{props.children}</div>
}

export default function DocsPage() {
  const sections: DocSection[] = [
    {
      id: 'overview',
      title: '技术文档（Docs）',
      subtitle:
        '面向合作方与开发者的可读技术文档：概念、接口、执行语义、回执字段与可验证示例。文档以 “402 → 谈判 → 成交 → 执行 → 回执” 为主线。',
      body: (
        <DocCard>
          <div className="grid gap-4 md:grid-cols-3">
            <Info title="核心承诺" desc="目标链目标资产精确到账（Exact Amount）" />
            <Info title="约束语义" desc="MaxTotalFee / MaxSlippage / MaxDuration / Deadline" />
            <Info title="可审计输出" desc="每一步的 tx、费用拆解、补足与最终交付" />
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <Pill>第三方 x402 兼容：不依赖私有字段</Pill>
            <Pill>执行与推理分离：计划可解释、执行可观测</Pill>
            <Pill>幂等与重试：orderId + stepId 防双花</Pill>
            <Pill>失败路径：超时、熔断、退款可解释</Pill>
          </div>
        </DocCard>
      ),
    },
    {
      id: 'concepts',
      title: '核心概念（Concepts）',
      subtitle: '用最少的概念理解整条链路：x402 输入、标准化请求、执行计划、回执与 Agent 分工。',
      body: (
        <div className="grid gap-5">
          <DocCard>
            <div className="text-sm font-semibold text-white/90">x402 Challenge → PaymentRequirement</div>
            <p className="mt-2 text-sm leading-relaxed text-white/65">
              服务方返回 HTTP 402（headers + body）。我们只做“捕获与标准化”：把不同格式归一成同一个{' '}
              <span className="text-white/85">PaymentRequirement</span>（链、资产、金额、收款方、过期时间、nonce），并保留原始载荷作为证据。
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>兼容嵌套字段：paymentRequirement / payment_requirement / requirement</li>
              <li>兼容数组字段：payment_requirements / paymentRequirements / requirements（按 caip2+asset_id 选最匹配项）</li>
              <li>严格模式（当提供 x402_request 时默认启用）：校验 recipient、amount_atomic、expires_at（RFC3339）</li>
            </ul>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <CodeBlock title="x402 示例（嵌套对象）" code={exampleX402Nested} />
              <CodeBlock title="x402 示例（数组多选）" code={exampleX402Array} />
            </div>
            <div className="mt-4">
              <CodeBlock title="严格模式常见错误码（示例）" code={exampleX402StrictErrors} />
            </div>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">ExecutionPlan（执行计划）</div>
            <p className="mt-2 text-sm leading-relaxed text-white/65">
              成交后生成 ExecutionPlan：包含 deadline 与 maxInput，以及由多个 steps 组成的可执行步骤序列（swap / bridge / pay）。
              每个 step 都有结构化 tx_template，便于 Executor/Monitor 消费与审计。
            </p>
            <div className="mt-4">
              <CodeBlock title="ExecutionPlan 示例（JSON）" code={exampleExecutionPlan} />
            </div>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">ReceiptV2（回执）</div>
            <p className="mt-2 text-sm leading-relaxed text-white/65">
              回执回答 “发生了什么、花了多少、是否兑现承诺”：它包含 paymentTarget、transactions、fees、supplement 与可选 x402Proof。
              在 Exact Amount 语义下，我们要求 <span className="text-white/85">actualOutAtomic == target amountAtomic</span>（不足则补齐；超阈值则熔断并走退款/失败路径）。
            </p>
            <div className="mt-4">
              <CodeBlock title="ReceiptV2 示例（JSON）" code={exampleReceiptV2} />
            </div>
          </DocCard>
        </div>
      ),
    },
    {
      id: 'agents',
      title: 'Agents 执行分层（Deterministic + Observable）',
      subtitle:
        '我们用多个 Agent 把执行拆开：报价、路由、执行、监控、记账。对外暴露的是稳定的计划与回执；对内协作建议使用 A2A + X401。',
      body: (
        <div className="grid gap-5 md:grid-cols-2">
          <DocCard>
            <div className="text-sm font-semibold text-white/90">Quote Agent</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>拉取 Jupiter / deBridge / Mayan / CCTP 报价</li>
              <li>输出最优与最坏情形（worst-case）并标记风险</li>
              <li>统一到相同的输入/输出模型，便于后续裁剪与比较</li>
            </ul>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">Route Agent</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>在约束下选择执行路径（优先 exact-output / exact-amount）</li>
              <li>输出 fallback 与触发条件（超时/滑点/成本异常）</li>
              <li>生成稳定、结构化的 ExecutionPlan</li>
            </ul>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">Execution Agent</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>按 step 顺序提交交易（swap / bridge intent / destination pay）</li>
              <li>幂等键：orderId + stepId，允许安全重试不双花</li>
              <li>只在需要时触发钱包签名，其他步骤由执行层完成</li>
            </ul>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">Monitor + Accounting</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>监听桥事件 / vault 事件 / 余额变化，计算 actualOut 与 Δ 补足</li>
              <li>成本拆解：平台费 / 桥费 / gas / 补足 / 超额回流</li>
              <li>生成可验证 Receipt（含证据与时间点）</li>
            </ul>
          </DocCard>
        </div>
      ),
    },
    {
      id: 'a2a-x401',
      title: 'A2A + X401：Agent 协作协议（建议）',
      subtitle:
        '当 Agent 变多、链路更长、需要可靠重试与审计时，推荐用 A2A 作为“消息信封”，并用 X401 做“点对点授权/鉴权”。',
      body: (
        <div className="grid gap-5">
          <DocCard>
            <div className="text-sm font-semibold text-white/90">为什么是 A2A</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>把“谁在调用谁 + 调用意图 + 约束 + 载荷 + 追踪”放进统一信封，天然可观测</li>
              <li>内置幂等键（idempotencyKey），支持重试与去重，避免重复执行</li>
              <li>对外系统只看 ExecutionPlan/Receipt，对内 Agent 可以独立演进</li>
            </ul>
            <div className="mt-4">
              <CodeBlock title="A2A Envelope（JSON 示例）" code={exampleA2AEnvelope} />
            </div>
          </DocCard>

          <DocCard>
            <div className="text-sm font-semibold text-white/90">为什么需要 X401（而不是把权限写死在网络层）</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>Agent-to-Agent 调用需要“按 scope 授权”，比如只允许 quote.route、禁止执行类动作</li>
              <li>需要可撤销、可轮换的 keyId，以及短 TTL 的授权，降低泄露风险</li>
              <li>把授权与审计绑定到 messageId/correlationId，便于追踪与问责</li>
            </ul>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <CodeBlock title="X401 Challenge（401 响应示例）" code={exampleX401Challenge} />
              <CodeBlock title="X401 Authorization（请求示例）" code={exampleX401Authorization} />
            </div>
          </DocCard>

          <DocCard>
            <div className="text-sm font-semibold text-white/90">X401 签名规范（Canonical）</div>
            <p className="mt-2 text-sm text-white/65">
              当前实现的签名串包含 method/path/nonce/bodyHash/keyId，服务端会对 body 做 SHA-256 后再验签。对接方只需要保证 canonical 一致即可。
            </p>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <CodeBlock title="Canonical（示例）" code={exampleX401Canonical} />
              <CodeBlock title="X401_KEYS_JSON（示例）" code={exampleX401KeysJson} />
            </div>
            <div className="mt-4">
              <CodeBlock title="本地可选持久化与审计（环境变量）" code={exampleA2AStorageEnv} />
            </div>
          </DocCard>

          <DocCard>
            <div className="text-sm font-semibold text-white/90">落地建议（与当前系统对齐）</div>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-white/65">
              <li>把 Quote/Route/Execution/Monitor/Accounting 都封装成独立的 A2A 服务端点（HTTP 或队列）。</li>
              <li>Orchestrator 只负责编排与状态机：为每个 step 生成 idempotencyKey，并记录事件流。</li>
              <li>X401 在每个 Agent 边界做校验：keyId → agent identity → scope 白名单 → TTL/nonce 去重。</li>
              <li>回执统一产物：ReceiptV2 从事件流与链上证据聚合，作为对外 proof 的基础。</li>
            </ol>
          </DocCard>

          <DocCard>
            <div className="text-sm font-semibold text-white/90">谈判 Realtime（WebSocket）</div>
            <p className="mt-2 text-sm text-white/65">
              服务端提供 WebSocket 推送 rounds，App 可订阅该通道实现 8 轮实时渲染，并保留 HTTP polling 兜底。
            </p>
            <div className="mt-4">
              <CodeBlock title="ws: /ws/negotiation/{sessionId}（消息示例）" code={exampleNegotiationWebSocket} />
            </div>
          </DocCard>
        </div>
      ),
    },
    {
      id: 'api',
      title: 'HTTP API（示例）',
      subtitle: '以下示例用于本地联调（negotiation-server 默认 8081）。生产环境会增加鉴权、限流与更严格的 proof。',
      body: (
        <div className="grid gap-5">
          <DocCard>
            <div className="text-sm font-semibold text-white/90">1) Start：提交 x402 与 persona_digest</div>
            <p className="mt-2 text-sm text-white/65">
              输入包含 x402 原始载荷（headers + body）。系统标准化后开启会话，返回 session_id 与约束信息。
            </p>
            <div className="mt-4">
              <CodeBlock title="curl: /api/v1/negotiation/start" code={curlStart} />
            </div>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">2) Commit：生成订单与执行计划</div>
            <p className="mt-2 text-sm text-white/65">
              Commit 返回 execution_plan 和 user_signing_payload（message + chain）。用户需要对该 message 做一次签名授权。
            </p>
            <div className="mt-4">
              <CodeBlock title="curl: /api/v1/negotiation/commit" code={curlCommit} />
            </div>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">3) Authorize：一次签名授权（执行前必需）</div>
            <p className="mt-2 text-sm text-white/65">
              执行前必须提交授权签名，否则执行接口会返回 403 missing_authorization。
            </p>
            <div className="mt-4">
              <CodeBlock title="curl: /api/v1/orders/authorize" code={curlAuthorize} />
            </div>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">4) Proof：生成可回填的 x402 proof（示例）</div>
            <p className="mt-2 text-sm text-white/65">
              对接方通常需要一个可验证的 proof（例如 header/body）。MVP 阶段可先以 ReceiptV2 作为 proof 基础，并由 Adapter 映射成对方需要的格式。
            </p>
            <div className="mt-4">
              <CodeBlock title="curl: /api/v1/x402/proof/{orderId}" code={curlX402Proof} />
            </div>
          </DocCard>
        </div>
      ),
    },
    {
      id: 'examples',
      title: '失败与回执样例（Examples）',
      subtitle: '成功不是唯一验收点：失败/熔断/退款必须可解释，并能对外输出一致的回执与证据。',
      body: (
        <div className="grid gap-5">
          <DocCard>
            <div className="text-sm font-semibold text-white/90">补足超阈值：FAILED + SUPPLEMENT_LIMIT</div>
            <div className="mt-4">
              <CodeBlock title="ReceiptV2（FAILED）" code={exampleReceiptFailed} />
            </div>
          </DocCard>
          <DocCard>
            <div className="text-sm font-semibold text-white/90">超时/主动取消：REFUNDED</div>
            <div className="mt-4">
              <CodeBlock title="ReceiptV2（REFUNDED）" code={exampleReceiptRefunded} />
            </div>
          </DocCard>
        </div>
      ),
    },
    {
      id: 'integration',
      title: '集成指南（对接方视角）',
      subtitle: '把你现有的 402 支付流程接入 Soulon：你保持 402 兼容输出，我们负责谈判、编排、执行与回执。',
      body: (
        <DocCard>
          <ol className="list-decimal space-y-3 pl-5 text-sm text-white/70">
            <li>
              服务方返回标准 402 challenge（headers + body），不要依赖私有字段；尽量包含 recipient、amount、asset、expiresAt、nonce。
            </li>
            <li>客户端捕获 402 后将原始载荷传入 start（x402_request）。</li>
            <li>谈判结束后 commit，拿到 execution_plan 与 user_signing_payload。</li>
            <li>用户对 user_signing_payload.message 签名一次，并提交 orders/authorize。</li>
            <li>按 execution_plan.steps 执行（swap/bridge/pay），每步回传 tx hash，最终拿到 ReceiptV2。</li>
          </ol>
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-xs font-semibold text-white/80">验收要点（MVP）</div>
            <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-white/65">
              <li>报价可解释：平台费 / 桥费 / gas / 预期补足清晰可见</li>
              <li>精确到账：Receipt 显示 actualOut == targetAmount</li>
              <li>可追溯：每个 step 的 tx 可查，能复盘失败原因</li>
            </ul>
          </div>
        </DocCard>
      ),
    },
  ]

  return (
    <main className="bg-bg">
      <div className="container-x py-14 md:py-18">
        <div className="grid gap-8 md:grid-cols-12">
          <aside className="md:col-span-3">
            <div className="sticky top-24 rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="text-xs font-semibold text-white/70">目录</div>
              <nav className="mt-3 grid gap-2 text-sm">
                {sections.map((s) => (
                  <a key={s.id} className="link" href={`#${s.id}`}>
                    {s.title}
                  </a>
                ))}
              </nav>
              <div className="mt-5 text-xs text-white/45">Last updated: {new Date().toISOString().slice(0, 10)}</div>
            </div>
          </aside>

          <div className="md:col-span-9">
            <SectionHeader title="Docs" subtitle="Readable, detailed technical docs and examples." kicker="Technical documentation" />
            <div className="mt-8 grid gap-10">
              {sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <div className="flex flex-col gap-3">
                    <div className="text-xl font-semibold tracking-tight md:text-2xl">{s.title}</div>
                    {s.subtitle ? <div className="text-sm text-white/60">{s.subtitle}</div> : null}
                  </div>
                  <div className="mt-5">{s.body}</div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

function Info(props: { title: string; desc: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <div className="text-xs font-semibold text-white/70">{props.title}</div>
      <div className="mt-2 text-sm text-white/80">{props.desc}</div>
    </div>
  )
}

function Pill(props: { children: string }) {
  return <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-white/70">{props.children}</div>
}
